import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import "./text-input.css";

export interface TextInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "prefix"> {
  label: string;
  hint?: string;
  error?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  /**
   * Visually hides the label (kept for screen readers). Used by dense
   * repeated rows (e.g. <ManualLineDraftRow>) where a group heading already
   * names the fields but each input still needs an accessible name.
   */
  hideLabel?: boolean;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { label, hint, error, prefix, suffix, hideLabel, id, type, className, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(" ") ||
    undefined;

  return (
    <div
      className={["wp-input", error ? "wp-input--error" : null, className]
        .filter(Boolean)
        .join(" ")}
    >
      <label
        htmlFor={inputId}
        className={hideLabel ? "visually-hidden" : "wp-input__label"}
      >
        {label}
      </label>
      <div className="wp-input__field">
        {prefix ? <span className="wp-input__adornment">{prefix}</span> : null}
        <input
          ref={ref}
          id={inputId}
          type={type ?? "text"}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={describedBy}
          {...rest}
        />
        {suffix ? <span className="wp-input__adornment">{suffix}</span> : null}
      </div>
      {error ? (
        <p id={errorId} className="wp-input__error" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="wp-input__hint">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
